import { createLogger } from "@/utils/logger";
import { Component, Fragment, State, h } from "@stencil/core";
import { Input } from "../input/input";
import {
  ActionTypes,
  SearchFlightState,
  dispatch,
  searchFlightStore,
} from "@/core/stores/searchflight.store";
import { SeatClass } from "@/core/models/searchflight";
import { Airport } from "@/core/models/airport";
import { theme } from "@/utils/theme";

@Component({
  tag: "pace-search-form",
  styleUrl: "search-form.scss",
  shadow: true,
})
export class SearchFormComponent {
  private logger = createLogger(this.constructor.name);

  @State() searchFlights: SearchFlightState;

  componentWillLoad() {
    searchFlightStore.subscribe((searchFlights) => {
      this.searchFlights = { ...searchFlights };
    });
  }

  async handleSubmit(event: Event) {
    event.preventDefault();
    dispatch({ type: ActionTypes.SEARCH_FLIGHTS });
  }

  async handleReset(event: Event) {
    event.preventDefault();
    this.logger.log("Resetting form...");
    dispatch({ type: ActionTypes.RESET });
  }

  mapAirports(
    airports: Airport[],
  ): { value: string | number; label: string }[] {
    return airports.map((airport) => {
      return {
        value: airport.code,
        label: `${airport.name} (${airport.code})`,
      };
    });
  }
  getSelectStyle() {
    return {
      input: theme.getInputStyle(),
      options: theme.getOptionsStyle(),
      selectedOption: theme.getContentStyle(),
      highlightedOption: theme.getHighlightedOptionStyle(),
    };
  }

  renderFlightNumberInputs() {
    return (
      <Fragment>
        <div class="row">
          <div class="form-control col-4">
            <Input
              style={theme.getInputStyle()}
              type="text"
              label="Flight Number"
              key="flightnumber"
              value={this.searchFlights?.searchFlight.flightNumber}
              readonly={this.searchFlights.submitted}
              required={true}
              onChange={(e) =>
                dispatch({
                  type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                  payload: {
                    flightNumber: e.target.value,
                  },
                })
              }
            />
            <a
              style={theme.getLinkStyle()}
              onClick={() =>
                dispatch({
                  type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                  payload: {
                    basicSearch: false,
                  },
                })
              }
            >
              I don't know the flight number
            </a>
          </div>
          <div class="form-control col-4">
            <Input
              style={theme.getInputStyle()}
              type="number"
              label="Number of Flights"
              key="numberofflights"
              min={1}
              max={5}
              required={true}
              value={this.searchFlights?.searchFlight.numberOfFlights}
              readonly={this.searchFlights.submitted}
              onChange={(e) =>
                dispatch({
                  type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                  payload: {
                    numberOfFlights: e.target.value,
                  },
                })
              }
            />
          </div>
        </div>
      </Fragment>
    );
  }

  renderAirportInputs() {
    return (
      <Fragment>
        <div class="row">
          <pace-select
            class="form-control col-4"
            label="Airline"
            name="airline"
            selectStyle={this.getSelectStyle()}
            searchable={true}
            required={true}
            value={this.searchFlights?.searchFlight.airline}
            readonly={this.searchFlights.submitted}
            options={this.searchFlights.airlines.map((airline) => {
              return {
                value: airline.code,
                label: airline.name,
              };
            })}
            loading={this.searchFlights.loadingAirlines}
            onChange={(e) =>
              dispatch({
                type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                payload: {
                  airline: e.target.value,
                },
              })
            }
            onInput={(e) =>
              dispatch({
                type: ActionTypes.SEARCH_AIRLINES,
                payload: e.target.value,
              })
            }
          />
          <div class="form-control col-4">
            <Input
              style={theme.getInputStyle()}
              type="number"
              label="Number of Flights"
              key="numberofflights"
              min={1}
              max={5}
              required={true}
              value={this.searchFlights?.searchFlight.numberOfFlights}
              readonly={this.searchFlights.submitted}
              onChange={(e) =>
                dispatch({
                  type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                  payload: {
                    numberOfFlights: e.target.value,
                  },
                })
              }
            />
          </div>
        </div>
        <div class="row">
          <pace-select
            class="form-control col-4"
            label="From"
            name="from"
            selectStyle={this.getSelectStyle()}
            searchable={true}
            required={true}
            value={this.searchFlights?.searchFlight.departureAirport}
            readonly={this.searchFlights.submitted}
            options={this.mapAirports(this.searchFlights.originAirports)}
            loading={this.searchFlights.loadingOriginAirports}
            onChange={(e) =>
              dispatch({
                type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                payload: {
                  departureAirport: e.target.value,
                },
              })
            }
            onInput={(e) =>
              dispatch({
                type: ActionTypes.SEARCH_ORIGIN_AIRPORT,
                payload: e.target.value,
              })
            }
          />
          <pace-select
            class="form-control col-4"
            label="To"
            name="to"
            selectStyle={this.getSelectStyle()}
            searchable={true}
            required={true}
            value={this.searchFlights?.searchFlight.arrivalAirport}
            readonly={this.searchFlights.submitted}
            options={this.mapAirports(this.searchFlights.destinationAirports)}
            loading={this.searchFlights.loadingDestinationAirports}
            onChange={(e) =>
              dispatch({
                type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                payload: {
                  arrivalAirport: e.target.value,
                },
              })
            }
            onInput={(e) =>
              dispatch({
                type: ActionTypes.SEARCH_DESTINATION_AIRPORT,
                payload: e.target.value,
              })
            }
          />
        </div>
      </Fragment>
    );
  }

  render() {
    return (
      <Fragment>
        <form
          onSubmit={async (e) => await this.handleSubmit(e)}
          class="container"
        >
          {this.searchFlights?.searchFlight.basicSearch &&
            this.renderFlightNumberInputs()}
          {!this.searchFlights?.searchFlight.basicSearch &&
            this.renderAirportInputs()}
          <div class="row">
            <pace-select
              class="form-control col-4"
              label="Seat Class"
              name="seatclass"
              selectStyle={this.getSelectStyle()}
              required={true}
              value={this.searchFlights?.searchFlight.seatClass?.valueOf()}
              readonly={this.searchFlights.submitted}
              options={[
                { value: SeatClass.Economy.valueOf(), label: "Economy" },
                {
                  value: SeatClass.ComfortEconomy.valueOf(),
                  label: "Comfort Economy",
                },
                {
                  value: SeatClass.PremiumEconomy.valueOf(),
                  label: "Premium Economy",
                },
                { value: SeatClass.Business.valueOf(), label: "Business" },
                { value: SeatClass.First.valueOf(), label: "First" },
              ]}
              onChange={(e) =>
                dispatch({
                  type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                  payload: {
                    seatClass: e.target.value,
                  },
                })
              }
            />
            <div class="form-control col-4">
              <Input
                style={theme.getInputStyle()}
                type="number"
                label="Number of passengers"
                key="numberofpassengers"
                value={this.searchFlights?.searchFlight.numberOfPassengers}
                readonly={this.searchFlights.submitted}
                min={1}
                max={5}
                required={true}
                onChange={(e) =>
                  dispatch({
                    type: ActionTypes.UPDATE_SEARCH_FLIGHTS,
                    payload: {
                      numberOfPassengers: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
          {!this.searchFlights.submitted && (
            <div class="row">
              <div class="col-8">
                <button
                  style={theme.getSecondaryButtonStyle()}
                  type="button"
                  hidden={this.searchFlights.searchFlight.basicSearch}
                  onClick={(e) => this.handleReset(e)}
                  class="button button-secondary"
                >
                  Back
                </button>
                <input
                  style={theme.getPrimaryButtonStyle()}
                  type="submit"
                  class="button button-primary"
                  value="Calculate"
                />
              </div>
            </div>
          )}
        </form>
        <section class="buttons row">
          <div class="col-8">
            <button
              class="button button-secondary"
              style={theme.getSecondaryButtonStyle()}
              hidden={!this.searchFlights.displayAircraftDiagram}
              onClick={() => dispatch({ type: ActionTypes.SWITCH_RESULTS_TAB })}
            >
              Back
            </button>
            <button
              class="button button-primary"
              style={theme.getPrimaryButtonStyle()}
              hidden={!this.searchFlights.submitted}
              onClick={() => dispatch({ type: ActionTypes.RESET })}
            >
              Start again
            </button>
          </div>
        </section>
      </Fragment>
    );
  }
}
